import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { signaturePubliqueApi } from '../../utils/api';

// Pavé de signature (tracé au doigt / souris)
function SignaturePad({ onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2; ctx.lineCap = 'round'; ctx.strokeStyle = '#1e2d4a';
  }, []);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const p = e.touches ? e.touches[0] : e;
    return { x: p.clientX - r.left, y: p.clientY - r.top };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; last.current = pos(e); };
  const move = (e) => {
    if (!drawing.current) return; e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = pos(e);
    ctx.beginPath(); ctx.moveTo(last.current.x, last.current.y); ctx.lineTo(p.x, p.y); ctx.stroke();
    last.current = p; if (!hasInk) setHasInk(true);
  };
  const end = () => { if (drawing.current) { drawing.current = false; onChange(hasInk ? canvasRef.current.toDataURL('image/png') : null); } };
  const clear = () => { const c = canvasRef.current; c.getContext('2d').clearRect(0, 0, c.width, c.height); setHasInk(false); onChange(null); };

  return (
    <div>
      <div style={{ position: 'relative' }}>
        <canvas ref={canvasRef}
          style={{ width: '100%', height: 160, border: '1px solid #d8dee9', borderRadius: 10, touchAction: 'none', background: '#fff' }}
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
        {!hasInk && <span style={{ position: 'absolute', top: 66, left: 0, right: 0, textAlign: 'center', color: '#aab2c0', fontSize: 13, pointerEvents: 'none' }}>Signez ici avec votre doigt ou la souris</span>}
      </div>
      <button type="button" onClick={clear} style={{ marginTop: 6, background: 'none', border: 'none', color: '#64748b', fontSize: 12, textDecoration: 'underline', cursor: 'pointer' }}>Effacer</button>
    </div>
  );
}

export default function SignerPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [nom, setNom] = useState('');
  const [lu, setLu] = useState(false);
  const [consent, setConsent] = useState(false);
  const [sig, setSig] = useState(null);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [pendingMsg, setPendingMsg] = useState('');

  const load = useCallback(() => {
    signaturePubliqueApi.get(token)
      .then(({ data }) => { setData(data); setNom(data.signataire || ''); if (data.statut === 'signe') setDone(true); })
      .catch(e => setErr(e.response?.data?.error || 'Lien de signature invalide ou expiré.'))
      .finally(() => setLoading(false));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const demanderCode = async () => {
    setBusy(true); setMsg('');
    try { const r = await signaturePubliqueApi.otp(token); setOtpSent(true); setMsg(r.data.message || 'Code envoyé.'); }
    catch (e) { setMsg(e.response?.data?.error || 'Erreur'); }
    setBusy(false);
  };
  const signer = async () => {
    setMsg('');
    if (!nom.trim()) return setMsg('Indiquez votre nom et prénom.');
    if (!lu) return setMsg('Cochez « Lu et approuvé ».');
    if (!consent) return setMsg('Acceptez de signer par voie électronique.');
    if (!sig) return setMsg('Ajoutez votre signature.');
    if (!otp.trim()) return setMsg('Saisissez le code reçu par e-mail.');
    setBusy(true);
    try {
      const r = await signaturePubliqueApi.signer(token, { nom: nom.trim(), signature_image: sig, otp: otp.trim(), mention: 'Lu et approuvé', consent: true });
      if (r.data?.pending) setPendingMsg(r.data.message || 'Votre signature est enregistrée. En attente des autres signataires.');
      setDone(true);
    } catch (e) { setMsg(e.response?.data?.error || 'Signature impossible'); }
    setBusy(false);
  };

  const wrap = { maxWidth: 620, margin: '0 auto', padding: 16, fontFamily: '-apple-system,Segoe UI,Roboto,Arial,sans-serif', color: '#1e2d4a' };

  if (loading) return <div style={{ ...wrap, textAlign: 'center', paddingTop: 60, color: '#94a3b8' }}>Chargement…</div>;
  if (err) return <div style={wrap}><div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 12, padding: 20, textAlign: 'center' }}>{err}</div></div>;

  if (done) return (
    <div style={wrap}>
      <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 14, padding: 28, textAlign: 'center' }}>
        <div style={{ fontSize: 40 }}>✓</div>
        <h2 style={{ margin: '8px 0', color: '#047857' }}>Signature enregistrée</h2>
        {pendingMsg
          ? <p style={{ color: '#065f46' }}>{pendingMsg} Une copie vous sera envoyée par e-mail dès que tous les signataires auront signé.</p>
          : <p style={{ color: '#065f46' }}>Merci ! Votre {data?.doc_label?.toLowerCase()} a bien été signé. Une copie vous est envoyée par e-mail et conservée par {data?.structure}.</p>}
      </div>
    </div>
  );

  return (
    <div style={wrap}>
      <div style={{ background: '#1e2d4a', color: '#fff', borderRadius: '14px 14px 0 0', padding: '18px 22px' }}>
        <div style={{ fontSize: 13, opacity: 0.85 }}>{data.structure}</div>
        <div style={{ fontSize: 20, fontWeight: 700 }}>Signer : {data.doc_label}</div>
        {data.enfant && <div style={{ fontSize: 13, opacity: 0.85, marginTop: 2 }}>Concernant {data.enfant}</div>}
        {data.co_signataires && data.co_signataires.length > 1 && (
          <div style={{ fontSize: 12, opacity: 0.8, marginTop: 8, borderTop: '1px solid rgba(255,255,255,.15)', paddingTop: 8 }}>
            Vous signez en tant que <strong>{data.role_label}</strong>. Signataires : {data.co_signataires.map(c => `${c.label}${c.signe ? ' ✓' : ''}`).join(' · ')}
          </div>
        )}
      </div>
      <div style={{ border: '1px solid #eef2f7', borderTop: 'none', borderRadius: '0 0 14px 14px', padding: 20 }}>
        <p style={{ fontSize: 13, color: '#64748b', marginTop: 0 }}>Veuillez lire le document ci-dessous, puis le signer.</p>
        <iframe title="Document" src={signaturePubliqueApi.documentUrl(token)} style={{ width: '100%', height: 420, border: '1px solid #e2e8f0', borderRadius: 10 }} />

        <div style={{ marginTop: 20 }}>
          <label style={{ fontSize: 13, fontWeight: 600 }}>Nom et prénom du signataire</label>
          <input value={nom} onChange={e => setNom(e.target.value)} placeholder="Votre nom et prénom"
            style={{ width: '100%', boxSizing: 'border-box', border: '1px solid #d8dee9', borderRadius: 10, padding: '10px 12px', fontSize: 15, marginTop: 4 }} />
        </div>

        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 14, fontSize: 14, cursor: 'pointer' }}>
          <input type="checkbox" checked={lu} onChange={e => setLu(e.target.checked)} style={{ marginTop: 3 }} />
          <span>Je déclare avoir <strong>lu et approuvé</strong> l'intégralité du document.</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 10, fontSize: 14, cursor: 'pointer' }}>
          <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} style={{ marginTop: 3 }} />
          <span>J'<strong>accepte de signer ce document par voie électronique</strong> et je reconnais que cette signature a la même valeur qu'une signature manuscrite.</span>
        </label>
        <p style={{ marginTop: 10, fontSize: 11.5, color: '#94a3b8', lineHeight: 1.5 }}>
          Signature électronique avancée (art. 1366-1367 du Code civil ; règlement eIDAS n° 910/2014). Pour constituer la preuve, sont enregistrés : votre e-mail, la validation d'un code à usage unique, l'horodatage, votre adresse IP et votre navigateur, ainsi qu'une empreinte du document (intégrité). Un <strong>certificat de signature</strong> est annexé au document signé et une copie vous parvient par e-mail via la crèche.
        </p>

        <div style={{ marginTop: 16 }}>
          <label style={{ fontSize: 13, fontWeight: 600 }}>Votre signature</label>
          <div style={{ marginTop: 4 }}><SignaturePad onChange={setSig} /></div>
        </div>

        {/* Vérification par code */}
        <div style={{ marginTop: 18, background: '#f8fafc', border: '1px solid #eef2f7', borderRadius: 12, padding: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Vérification de votre identité</div>
          {!otpSent ? (
            <>
              <p style={{ fontSize: 12.5, color: '#64748b', margin: '0 0 10px' }}>Nous vous envoyons un code à votre adresse e-mail ({data.email_masque}) pour confirmer la signature.</p>
              <button type="button" onClick={demanderCode} disabled={busy}
                style={{ background: '#1e2d4a', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>
                {busy ? 'Envoi…' : 'Recevoir le code par e-mail'}
              </button>
            </>
          ) : (
            <>
              <label style={{ fontSize: 12.5, color: '#64748b' }}>Code reçu par e-mail</label>
              <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                <input value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="6 chiffres" inputMode="numeric"
                  style={{ flex: '1 1 120px', border: '1px solid #d8dee9', borderRadius: 10, padding: '10px 12px', fontSize: 18, letterSpacing: 4, textAlign: 'center' }} />
                <button type="button" onClick={demanderCode} disabled={busy} style={{ background: 'none', border: '1px solid #d8dee9', borderRadius: 10, padding: '0 12px', fontSize: 12.5, color: '#64748b', cursor: 'pointer' }}>Renvoyer</button>
              </div>
            </>
          )}
        </div>

        {msg && <p style={{ fontSize: 13, color: msg.includes('envoyé') ? '#047857' : '#b91c1c', marginTop: 12 }}>{msg}</p>}

        <button type="button" onClick={signer} disabled={busy || !otpSent}
          style={{ width: '100%', marginTop: 18, background: '#c0985a', color: '#fff', border: 'none', borderRadius: 12, padding: '14px', fontSize: 16, fontWeight: 700, cursor: 'pointer', opacity: (busy || !otpSent) ? 0.55 : 1 }}>
          {busy ? 'Signature en cours…' : 'Signer le document'}
        </button>
        <p style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', marginTop: 10 }}>Signature électronique sécurisée · {data.structure} via Hello Georges</p>
      </div>
    </div>
  );
}
